import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { catchError, map, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';

// Most needed account information for display in the UI
export type AccountSummary = {
  givenName: string;
  familyName: string;
  email: string;
  gid: string;
  oid: string;
  roles: string[];
};

export enum UserRole {
  PSMI = 'PSMI',
  ADMIN = 'Admin'
}

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(MsalService);
  private readonly msalGuardConfig = inject(MSAL_GUARD_CONFIG) as MsalGuardConfiguration;

  userIsLoggedIn(): boolean {
    return this.getActiveAccount() !== null;
  }

  getActiveAccount() {
    const activeAccount = this.authService.instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const [firstAccount] = this.authService.instance.getAllAccounts();
    if (firstAccount) {
      this.authService.instance.setActiveAccount(firstAccount);
      return firstAccount;
    }

    return null;
  }

  readonly accountSummary = signal<AccountSummary | null>(null);
  readonly profilePhotoUrl = signal<string | null>(null);

  private buildAccountSummaryFromAzure(): AccountSummary | null {
    const activeAccount = this.getActiveAccount();
    if (!activeAccount) {
      return null;
    }
    if (!activeAccount.idTokenClaims) {
      console.warn('Active account does not have idTokenClaims');
      return null;
    }
    if (!activeAccount.idTokenClaims['gid']) {
      console.warn('Active account does not have gid claim');
      return null;
    }
    if (!activeAccount.idTokenClaims.roles) {
      console.warn('Active account does not have roles claim');
      return null;
    }
    if (!activeAccount.idTokenClaims['preferred_given_name']) {
      console.warn('Active account does not have preferred_given_name claim');
      return null;
    }
    if (!activeAccount.idTokenClaims['preferred_family_name']) {
      console.warn('Active account does not have preferred_family_name claim');
      return null;
    }

    return {
      givenName: activeAccount.idTokenClaims?.['preferred_given_name'] as string,
      familyName: activeAccount.idTokenClaims?.['preferred_family_name'] as string,
      email: activeAccount.username,
      gid: (activeAccount.idTokenClaims?.['gid'] as string).toLowerCase(),
      oid: activeAccount.idTokenClaims?.oid as string,
      roles: activeAccount.idTokenClaims?.roles as string[]
    };
  }

  initializeAccountSummary(): void {
    this.getActiveAccount();
    this.accountSummary.set(this.buildAccountSummaryFromAzure());
  }

  loginViaRedirect(): void {
    if (!this.msalGuardConfig.authRequest) {
      // Should be defined in app.module.ts
      throw new Error('MSAL authRequest is required');
    }

    const userHasMultipleAccounts = this.authService.instance.getAllAccounts().length > 1;
    this.authService.loginRedirect({
      ...this.msalGuardConfig.authRequest,
      // If there are multiple accounts, prompt the user to select an account.
      // Otherwise, proceed with the default login behavior.
      ...(userHasMultipleAccounts && { prompt: 'select_account' })
    } as RedirectRequest);
  }

  getProfilePicture(): Observable<string | null> {
    return this.http
      .get('https://graph.microsoft.com/v1.0/me/photo/$value', { responseType: 'blob' })
      .pipe(
        map(blob => URL.createObjectURL(blob)),
        catchError(() => of(null))
      );
  }

  createUserIfDoesNotExist() {
    const url = environment.apiURL + '/users/me';
    return this.http.put(url, null);
  }

  hasRequiredRoles(roles: UserRole[]): boolean {
    const userRoles = this.accountSummary()?.roles;

    if (!userRoles || userRoles.length === 0) {
      return false; // No roles available
    }

    // Check if any of the specified roles are present in the user's roles
    return roles.some(role => userRoles.includes(role));
  }

  hasDetailedSimulationResultsAccess(): boolean {
    return this.hasRequiredRoles([UserRole.ADMIN]);
  }
}
